import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaPlane, FaUsers, FaBriefcase, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { jobsAPI } from '../utils/api';
import JobCard from '../components/JobCard';
import './Home.css';

const COUNTRIES = ['UAE', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'Singapore', 'Malaysia', 'Libya'];
const COUNTRY_FLAGS = { 'UAE': '🇦🇪', 'Saudi Arabia': '🇸🇦', 'Kuwait': '🇰🇼', 'Qatar': '🇶🇦', 'Bahrain': '🇧🇭', 'Oman': '🇴🇲', 'Singapore': '🇸🇬', 'Malaysia': '🇲🇾', 'Libya': '🇱🇾' };

const STATS = [
  { icon: <FaBriefcase />, value: '500+', label: 'Jobs Placed' },
  { icon: <FaUsers />, value: '2000+', label: 'Candidates Served' },
  { icon: <FaMapMarkerAlt />, value: '9', label: 'Countries' },
  { icon: <FaPlane />, value: '10+', label: 'Years Experience' },
];

const WHY_US = [
  { title: 'Verified Employers', desc: 'We partner only with verified, reputable employers across the Gulf and Southeast Asia.' },
  { title: 'End-to-End Support', desc: 'From documentation to visa processing, we guide you through every step of the journey.' },
  { title: 'Transparent Process', desc: 'No hidden fees. Clear salary packages and job descriptions before you commit.' },
  { title: 'Aftercare Service', desc: 'Our support doesn\'t end at placement — we follow up to ensure your satisfaction.' },
];

const Home = () => {
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    jobsAPI.getAll({ limit: 6 }).then(res => setFeaturedJobs(res.data.jobs)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (country) params.set('country', country);
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="hero-section">
        <div className="hero-bg-pattern" />
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge"><FaPlane /> International Recruitment Agency</div>
            <h1 className="hero-title">
              Your Gateway to<br />
              <span className="hero-accent">Global Careers</span>
            </h1>
            <p className="hero-subtitle">
              Pavishna Global Services connects skilled professionals with top employers across UAE, Saudi Arabia, Qatar, Kuwait, and more. Start your international journey today.
            </p>

            <form className="hero-search" onSubmit={handleSearch}>
              <div className="search-field">
                <FaSearch className="search-icon" />
                <input
                  type="text" placeholder="Job role, skill or keyword..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="search-divider" />
              <div className="search-field">
                <FaMapMarkerAlt className="search-icon" />
                <select value={country} onChange={e => setCountry(e.target.value)} className="search-select">
                  <option value="">All Countries</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</option>)}
                </select>
              </div>
              <button type="submit" className="search-btn">Search Jobs</button>
            </form>

            <div className="hero-tags">
              <span>Popular:</span>
              {['Civil Engineer', 'Nurse', 'Driver', 'Chef', 'Welder', 'Accountant'].map(t => (
                <button key={t} onClick={() => navigate(`/jobs?search=${t}`)} className="tag-btn">{t}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-scroll-hint">
          <div className="scroll-dot" />
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="stat-item">
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries */}
      <section className="section-pad countries-section">
        <div className="container text-center">
          <h2 className="section-title"><span className="title-accent">Countries</span> We Serve</h2>
          <p className="section-subtitle">Pavishna Global Services operates in 9 countries, placing skilled workers in top roles across the Gulf region and Southeast Asia.</p>
          <div className="countries-grid">
            {COUNTRIES.map(c => (
              <button key={c} className="country-card" onClick={() => navigate(`/jobs?country=${c}`)}>
                <span className="country-flag">{COUNTRY_FLAGS[c]}</span>
                <span className="country-name">{c}</span>
                <FaArrowRight className="country-arrow" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="section-pad featured-section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title"><span className="title-accent">Latest</span> Vacancies</h2>
              <p className="section-subtitle" style={{ margin: 0 }}>Fresh job openings updated regularly across all our partner countries.</p>
            </div>
            <Link to="/jobs" className="btn-secondary">View All Jobs <FaArrowRight /></Link>
          </div>
          {featuredJobs.length > 0 ? (
            <div className="jobs-grid">
              {featuredJobs.map(job => <JobCard key={job.id} job={job} />)}
            </div>
          ) : (
            <div className="empty-jobs">
              <FaBriefcase size={48} />
              <p>New vacancies coming soon. Check back shortly!</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Us */}
      <section className="section-pad why-section">
        <div className="container">
          <div className="why-grid">
            <div className="why-text">
              <div className="eyebrow">Why Choose Us</div>
              <h2 className="section-title">A Recruitment Partner <span className="title-accent">You Can Trust</span></h2>
              <p style={{ color: 'var(--slate)', marginBottom: 32, lineHeight: 1.7 }}>
                With over a decade of experience, Pavishna Global Services has earned the trust of thousands of candidates and employers alike. We don't just fill positions — we build careers.
              </p>
              <div className="why-items">
                {WHY_US.map((w, i) => (
                  <div key={i} className="why-item">
                    <FaCheckCircle className="why-check" />
                    <div>
                      <strong>{w.title}</strong>
                      <p>{w.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary" style={{ marginTop: 32 }}>Get In Touch <FaArrowRight /></Link>
            </div>
            <div className="why-visual">
              <div className="why-card-float card-1">
                <FaPlane className="float-icon" />
                <div>
                  <strong>500+ Candidates</strong>
                  <span>Successfully placed abroad</span>
                </div>
              </div>
              <div className="why-img-wrap">
                <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=500&q=80" alt="Team" />
              </div>
              <div className="why-card-float card-2">
                <FaMapMarkerAlt className="float-icon" />
                <div>
                  <strong>9 Countries</strong>
                  <span>Active placement destinations</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container text-center">
          <h2>Ready to Work Abroad?</h2>
          <p>Browse our latest openings and take the first step toward your international career today.</p>
          <div className="cta-btns">
            <Link to="/jobs" className="btn-gold">Browse All Jobs</Link>
            <Link to="/register" className="btn-secondary" style={{ color: '#fff', borderColor: '#fff' }}>Register Free</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
